import { ApolloClient } from '@apollo/client';
import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  Details,
  type FormActionData,
  Loader,
  ReadOnlyField,
  type StationMessage,
  TextButton,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import { Field } from 'formik';
import {
  CreateForgotPasswordWebHookSecretDocument,
  type CreateForgotPasswordWebHookSecretMutation,
  type CreateForgotPasswordWebHookSecretMutationVariables,
  CreateMigrationWebHookSecretDocument,
  type CreateMigrationWebHookSecretMutation,
  type CreateMigrationWebHookSecretMutationVariables,
  CreateUserSignUpWebHookSecretDocument,
  type CreateUserSignUpWebHookSecretMutation,
  type CreateUserSignUpWebHookSecretMutationVariables,
  IdpUserStoreDocument,
  type IdpUserStoreQuery,
  type IdpUserStoreQueryVariables,
} from 'generated/axauth-service';
import { EnvironmentServiceState } from 'generated/id-service-env-administration';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import { environmentApolloClient } from 'modules/environmentApolloClient';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getManagedServiceData } from 'util/managedServiceUtil';
import { ensureError } from '../../../../../common/errors/ensure-error';

const webBookSecretDefault = '*********************************';

const hookTypeLabels: Record<string, string> = {
  userSignUp: 'User Sign-Up',
  forgotPassword: 'Forgot Password',
  migration: 'End User Migration',
};

export const AuthServiceWebHookSecret: React.FC = () => {
  const { userStoreId, envId, hookType } = useParams<{
    userStoreId: string;
    envId: string;
    hookType: string;
  }>();

  const hookTypeLabel = hookTypeLabels[hookType] ?? hookType;
  const defaultTitle = `${hookTypeLabel} Webhook Secret`;
  const subtitle = `${hookTypeLabel} Webhook Secret Details`;

  const [secretData, setSecretData] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [serviceState, setserviceState] = useState<
    EnvironmentServiceState | undefined
  >();
  const [data, setData] = useState<IdpUserStoreQuery>();

  const envApolloClient = useRef<ApolloClient<Record<string, unknown>>>();

  //Get Application webhook info
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        envApolloClient.current = environmentApolloClient(
          envId,
          new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
        );

        // Get IdpUserStore data
        const idpUserStoreResult = await envApolloClient.current.query<
          IdpUserStoreQuery,
          IdpUserStoreQueryVariables
        >({
          query: IdpUserStoreDocument,
          variables: {
            id: userStoreId,
          },
          fetchPolicy: 'no-cache',
        });

        setData(idpUserStoreResult.data);
      } catch (e) {
        const error = ensureError(e);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [userStoreId, envId]);

  //Get managed service data
  useEffect(() => {
    (async () => {
      const serviceData = await getManagedServiceData(
        envId,
        ManagedServiceId.AXAUTH_SERVICE,
      );
      setserviceState(serviceData?.state);
    })();
  }, [envId]);

  const { actions, newSecret } = useGenerationActions(
    userStoreId,
    envId,
    hookType,
    setLoading,
  );

  useEffect(() => {
    (async () => {
      try {
        if (
          serviceState === EnvironmentServiceState.Enabled &&
          data?.idpUserStore &&
          !data?.idpUserStore?.userSignUpWebhookSecretIsSet &&
          hookType === 'userSignUp'
        ) {
          const envApolloClientNew = environmentApolloClient(
            envId,
            new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
          );
          setLoading(true);
          // Query application
          const result = await envApolloClientNew.mutate<
            CreateUserSignUpWebHookSecretMutation,
            CreateUserSignUpWebHookSecretMutationVariables
          >({
            mutation: CreateUserSignUpWebHookSecretDocument,
            variables: {
              input: { userStoreId: userStoreId },
            },
            fetchPolicy: 'no-cache',
          });

          setSecretData(result.data?.generateUserSignUpWebhookSecret?.secret);
          if (result.errors !== undefined) {
            setError(result.errors[0]?.message);
          }
        } else if (
          serviceState === EnvironmentServiceState.Enabled &&
          data?.idpUserStore &&
          !data?.idpUserStore?.resetPasswordWebhookSecretIsSet &&
          hookType === 'forgotPassword'
        ) {
          const envApolloClientNew = environmentApolloClient(
            envId,
            new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
          );
          setLoading(true);
          // Query application
          const result = await envApolloClientNew.mutate<
            CreateForgotPasswordWebHookSecretMutation,
            CreateForgotPasswordWebHookSecretMutationVariables
          >({
            mutation: CreateForgotPasswordWebHookSecretDocument,
            variables: {
              input: { userStoreId: userStoreId },
            },
            fetchPolicy: 'no-cache',
          });

          setSecretData(
            result.data?.generateResetPasswordWebhookSecret?.secret,
          );
          if (result.errors !== undefined) {
            setError(result.errors[0]?.message);
          }
        } else if (
          serviceState === EnvironmentServiceState.Enabled &&
          data?.idpUserStore &&
          !data?.idpUserStore?.migrationWebhookSecretIsSet &&
          hookType === 'migration'
        ) {
          const envApolloClientNew = environmentApolloClient(
            envId,
            new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
          );
          setLoading(true);
          // Query application
          const result = await envApolloClientNew.mutate<
            CreateMigrationWebHookSecretMutation,
            CreateMigrationWebHookSecretMutationVariables
          >({
            mutation: CreateMigrationWebHookSecretDocument,
            variables: {
              input: { userStoreId: userStoreId },
            },
            fetchPolicy: 'no-cache',
          });

          setSecretData(result.data?.generateMigrationWebhookSecret?.secret);
          if (result.errors !== undefined) {
            setError(result.errors[0]?.message);
          }
        } else {
          setSecretData(webBookSecretDefault);
        }
      } catch (e) {
        const error = ensureError(e);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [data, envId, hookType, serviceState, userStoreId]);

  // Newly generated secret will be set here
  useEffect(() => {
    if (newSecret) {
      setSecretData(newSecret);
    }
  }, [newSecret]);

  const [message, setMessage] = useState<StationMessage>();
  useEffect(() => {
    if (secretData && secretData !== webBookSecretDefault) {
      setMessage({
        title:
          'WARNING: Please copy the webhook secret displayed below and store it in a secure location. Once this station is closed, this value cannot be retrieved again and can only be replaced by generating a new webhook secret.',
        type: 'warning',
      });
    }
  }, [secretData]);

  const Form: React.FC<{
    secret: string | undefined;
  }> = ({ secret }) => {
    const onCopyClickedHandler = () => (): void => {
      try {
        navigator.clipboard.writeText(secret ?? '');
      } catch (_error) {
        // eslint-disable-next-line no-console
        console.warn('Failed to copy to clipboard');
      }
    };
    return (
      <Loader showLoader={loading}>
        <div style={{ display: 'flex', gap: 50, alignItems: 'baseline' }}>
          <Field name="title" label={defaultTitle} as={ReadOnlyField} />
          <div>
            <ReadOnlyField value={secretData} />
          </div>
          <div>
            {secretData !== webBookSecretDefault && (
              <TextButton
                text="Copy"
                onButtonClicked={onCopyClickedHandler()}
              />
            )}
          </div>
        </div>
      </Loader>
    );
  };

  return (
    <>
      <Details
        defaultTitle={defaultTitle}
        subtitle={subtitle}
        actions={
          (hookType === 'userSignUp' &&
            data?.idpUserStore?.userSignUpWebhookSecretIsSet) ||
          (hookType === 'forgotPassword' &&
            data?.idpUserStore?.resetPasswordWebhookSecretIsSet) ||
          (hookType === 'migration' &&
            data?.idpUserStore?.migrationWebhookSecretIsSet)
            ? actions
            : undefined
        }
        initialData={{
          data: { secretData },
          loading: false,
          error: error,
        }}
        saveData={() => Promise.resolve()}
        stationMessage={message}
      >
        <Form secret={secretData} />
      </Details>
    </>
  );
};

const useGenerationActions = (
  userStoreId: string,
  envId: string,
  hookType: string,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
): {
  readonly actions: FormActionData[];
  readonly newSecret?: string;
} => {
  // Generate new client secret for the manifest webhook
  const [newSecret, setNewSecret] = useState<string>();
  const actions: FormActionData[] = [];

  const envApolloClientNew = environmentApolloClient(
    envId,
    new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
  );
  actions.push({
    label: 'Generate New Secret',
    confirmationMode: 'Advanced',
    confirmationConfig: {
      body: 'Previously used secret will become invalid',
    },
    onActionSelected: async () => {
      setLoading(true);

      if (hookType === 'userSignUp') {
        const result = await envApolloClientNew.mutate<
          CreateUserSignUpWebHookSecretMutation,
          CreateUserSignUpWebHookSecretMutationVariables
        >({
          mutation: CreateUserSignUpWebHookSecretDocument,
          variables: {
            input: { userStoreId: userStoreId },
          },
          fetchPolicy: 'no-cache',
        });
        setNewSecret(result.data?.generateUserSignUpWebhookSecret?.secret);
      } else if (hookType === 'forgotPassword') {
        const result = await envApolloClientNew.mutate<
          CreateForgotPasswordWebHookSecretMutation,
          CreateForgotPasswordWebHookSecretMutationVariables
        >({
          mutation: CreateForgotPasswordWebHookSecretDocument,
          variables: {
            input: { userStoreId: userStoreId },
          },
          fetchPolicy: 'no-cache',
        });
        setNewSecret(result.data?.generateResetPasswordWebhookSecret?.secret);
      } else if (hookType === 'migration') {
        const result = await envApolloClientNew.mutate<
          CreateMigrationWebHookSecretMutation,
          CreateMigrationWebHookSecretMutationVariables
        >({
          mutation: CreateMigrationWebHookSecretDocument,
          variables: {
            input: { userStoreId: userStoreId },
          },
          fetchPolicy: 'no-cache',
        });
        setNewSecret(result.data?.generateMigrationWebhookSecret?.secret);
      }

      setLoading(false);
    },
  });

  return {
    actions,
    newSecret,
  } as const;
};

export const AuthServiceWebhookSecretCrumb: BreadcrumbResolver = () => 'Secret';
